import { CircleAlert, CircleCheck, Info, type LucideIcon, TriangleAlert } from 'lucide-react';
import type React from 'react';

interface AlertProps {
  variant: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message?: string;
  children?: React.ReactNode; // custom content (a link, a retry button) after the title and message
}

const VARIANTS: Record<
  AlertProps['variant'],
  { container: string; icon: string; Icon: LucideIcon }
> = {
  success: {
    container: 'border-success-500 bg-success-50 dark:border-success-500/30 dark:bg-success-500/15',
    icon: 'text-success-500',
    Icon: CircleCheck,
  },
  error: {
    container: 'border-error-500 bg-error-50 dark:border-error-500/30 dark:bg-error-500/15',
    icon: 'text-error-500',
    Icon: CircleAlert,
  },
  warning: {
    container: 'border-warning-500 bg-warning-50 dark:border-warning-500/30 dark:bg-warning-500/15',
    icon: 'text-warning-500',
    Icon: TriangleAlert,
  },
  info: {
    container:
      'border-blue-light-500 bg-blue-light-50 dark:border-blue-light-500/30 dark:bg-blue-light-500/15',
    icon: 'text-blue-light-500',
    Icon: Info,
  },
};

/** Inline notice. Error and warning announce themselves (`role="alert"`); callers add no role of their own. */
export const Alert: React.FC<AlertProps> = ({ variant, title, message, children }) => {
  const { container, icon, Icon } = VARIANTS[variant];
  return (
    <div
      role={variant === 'error' || variant === 'warning' ? 'alert' : undefined}
      className={`flex items-start gap-3 rounded-xl border p-4 ${container}`}
    >
      <Icon className={`size-5 shrink-0 ${icon}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title && (
          <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">{title}</h4>
        )}
        {message && <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>}
        {children}
      </div>
    </div>
  );
};
